/**
 * Empresas: quem são, como se chamam e de que cor são.
 *
 * Até a fase A isso era uma lista fixa no código, o que obrigava um deploy para
 * cadastrar cliente. Agora cada empresa é um documento em `companies/{id}` com
 * `nome`, `cor` e `ativo`. O banco diz QUAL cor; a paleta abaixo continua no
 * código, porque são classes do Tailwind e porque texto vindo do banco nunca
 * deve virar atributo `class`.
 *
 * Duas formas de consumir:
 *   useEmpresas(ids)  → lista reativa, para telas que montam menu e seletor;
 *   rotuloEmpresa(id) → leitura síncrona do que já foi carregado, para os cantos
 *                       que só precisam do nome (modelo de WhatsApp, por exemplo)
 *                       e onde passar prop até lá custaria mais do que vale.
 */
import { useEffect, useMemo, useState } from 'react';
import { db } from './firebase';

export const CORES = ['red', 'orange', 'blue', 'green', 'purple', 'slate'];

const PALETA = {
  red: {
    active: 'bg-red-600 text-white',
    inactive: 'text-red-600 hover:bg-red-50 border border-red-300',
    navActive: 'bg-red-50 text-red-700', tabActive: 'bg-red-600 text-white',
    btn: 'bg-red-600 hover:bg-red-700 text-white', audit: 'bg-red-50 text-red-600',
    convBar: 'bg-red-500', headerBorder: 'border-t-[3px] border-red-600',
  },
  orange: {
    active: 'bg-orange-500 text-white',
    inactive: 'text-orange-600 hover:bg-orange-50 border border-orange-300',
    navActive: 'bg-orange-50 text-orange-700', tabActive: 'bg-orange-500 text-white',
    btn: 'bg-orange-500 hover:bg-orange-600 text-white', audit: 'bg-orange-50 text-orange-600',
    convBar: 'bg-orange-400', headerBorder: 'border-t-[3px] border-orange-500',
  },
  blue: {
    active: 'bg-blue-600 text-white',
    inactive: 'text-blue-600 hover:bg-blue-50 border border-blue-300',
    navActive: 'bg-blue-50 text-blue-700', tabActive: 'bg-blue-600 text-white',
    btn: 'bg-blue-600 hover:bg-blue-700 text-white', audit: 'bg-blue-100 text-blue-600',
    convBar: 'bg-blue-400', headerBorder: 'border-t-[3px] border-blue-600',
  },
  green: {
    active: 'bg-green-600 text-white',
    inactive: 'text-green-600 hover:bg-green-50 border border-green-300',
    navActive: 'bg-green-50 text-green-700', tabActive: 'bg-green-600 text-white',
    btn: 'bg-green-600 hover:bg-green-700 text-white', audit: 'bg-green-50 text-green-600',
    convBar: 'bg-green-400', headerBorder: 'border-t-[3px] border-green-600',
  },
  purple: {
    active: 'bg-purple-600 text-white',
    inactive: 'text-purple-600 hover:bg-purple-50 border border-purple-300',
    navActive: 'bg-purple-50 text-purple-700', tabActive: 'bg-purple-600 text-white',
    btn: 'bg-purple-600 hover:bg-purple-700 text-white', audit: 'bg-purple-50 text-purple-600',
    convBar: 'bg-purple-400', headerBorder: 'border-t-[3px] border-purple-600',
  },
  slate: {
    active: 'bg-slate-700 text-white',
    inactive: 'text-slate-600 hover:bg-slate-50 border border-slate-300',
    navActive: 'bg-slate-100 text-slate-700', tabActive: 'bg-slate-700 text-white',
    btn: 'bg-slate-700 hover:bg-slate-800 text-white', audit: 'bg-slate-100 text-slate-600',
    convBar: 'bg-slate-400', headerBorder: 'border-t-[3px] border-slate-700',
  },
};

export function paleta(cor) {
  return PALETA[cor] || PALETA.blue;
}

// Nomes já vistos nesta sessão. Existe só para o acesso síncrono; nunca é a
// fonte da verdade, e cair de volta no id é aceitável (o id é legível).
const registro = new Map();

export function rotuloEmpresa(id) {
  if (!id) return '';
  return registro.get(id) || id;
}

/** Monta o objeto de tela a partir do documento, tolerando campo faltando. */
function comoTela(id, dados) {
  const nome = (dados && dados.nome) || id;
  registro.set(id, nome);
  const cor = dados && CORES.includes(dados.cor) ? dados.cor : 'blue';
  return { id, label: nome, color: cor, ativo: !dados || dados.ativo !== false, ...paleta(cor) };
}

/**
 * Acompanha as empresas indicadas, uma assinatura por documento.
 * Um `get` por id em vez de listar a coleção: no produto multiempresa ninguém
 * pode enumerar os outros clientes, e as regras do Firestore refletem isso.
 */
export function useEmpresas(ids) {
  const chave = (ids || []).join(',');
  const lista = useMemo(() => (chave ? chave.split(',') : []), [chave]);
  const [mapa, setMapa] = useState({});

  useEffect(() => {
    if (!lista.length) { setMapa({}); return; }
    const parar = lista.map(id =>
      db.collection('companies').doc(id).onSnapshot(
        doc => setMapa(m => ({ ...m, [id]: comoTela(id, doc.exists ? doc.data() : null) })),
        // Sem permissão ou sem rede: mostra o id em vez de sumir com a empresa
        // do seletor, senão a pessoa perde o acesso ao próprio pipeline.
        () => setMapa(m => ({ ...m, [id]: comoTela(id, null) })),
      ));
    return () => parar.forEach(p => p());
  }, [lista]);

  // Preserva a ordem pedida, que é a ordem do cadastro do usuário.
  const empresas = useMemo(
    () => lista.map(id => mapa[id]).filter(Boolean).filter(e => e.ativo),
    [lista, mapa]);

  return { empresas, porId: mapa, carregando: lista.length > 0 && !Object.keys(mapa).length };
}

/** Uma empresa só, para as telas públicas (portal do parceiro e indicação). */
export function useEmpresa(id) {
  const [estado, setEstado] = useState({ empresa: null, carregando: !!id });
  useEffect(() => {
    if (!id) { setEstado({ empresa: null, carregando: false }); return; }
    let vivo = true;
    db.collection('companies').doc(id).get()
      .then(doc => { if (vivo) setEstado({ empresa: doc.exists ? comoTela(id, doc.data()) : null, carregando: false }); })
      .catch(() => { if (vivo) setEstado({ empresa: null, carregando: false }); });
    return () => { vivo = false; };
  }, [id]);
  return estado;
}
